import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInvoiceShareLink, emailInvoice } from '../services/email';
import { buildViberInvoiceUrl, buildWhatsAppInvoiceUrl } from '../utils/invoiceMessaging';
import ConfirmationModal from './modals/confirmation';
import { toast } from '../utils/toast';
import useCompanyMessaging from '../hooks/useCompanyMessaging';

const LINK_PLACEHOLDER = '{link}';

// Fill the company template's placeholders. {link} is only known once the
// secure share link is prepared, so it is substituted at send time.
export const renderInvoiceMessage = (template, { clientName, invoiceNumber, jobNumber }) =>
    String(template || '')
        .replace(/\{clientName\}/g, clientName)
        .replace(/\{invoiceNumber\}/g, invoiceNumber)
        .replace(/\{jobNumber\}/g, String(jobNumber));

// The share link goes where the template put {link}; when the template has no
// {link} it is appended so a chat message always carries the PDF.
export const withInvoiceLink = (message, url, viewLabel) =>
    message.includes(LINK_PLACEHOLDER)
        ? message.replace(new RegExp(LINK_PLACEHOLDER.replace(/[{}]/g, '\\$&'), 'g'), url)
        : `${message.trim()}\n\n${viewLabel}\n${url}`;

const InvoiceSharePanel = ({ invoice, onClose }) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const [messageEdited, setMessageEdited] = useState(false);
    const [recipient, setRecipient] = useState('');
    const [shareUrl, setShareUrl] = useState('');
    // Preparing the link issues the invoice number when the job has none yet.
    const [issuedNumber, setIssuedNumber] = useState('');
    const [linkError, setLinkError] = useState('');
    const [shared, setShared] = useState('');
    const [confirmSend, setConfirmSend] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [sending, setSending] = useState(false);

    const { invoiceMessageTemplate } = useCompanyMessaging();

    const jobId = invoice?.jobId ?? invoice?.id;
    // Never show a number that is not issued: the real one arrives with the link.
    const invoiceNumber = invoice?.invoiceNumber || issuedNumber || t('invoiceShare.invoiceFallback');
    const clientName = invoice?.clientName || invoice?.name || t('jobs.client');
    const hasPhone = !!String(invoice?.phone || '').replace(/\D/g, '');

    const prepareShareLink = async () => {
        if (!jobId) return;
        setShareUrl('');
        setLinkError('');
        setPreparing(true);
        try {
            const response = await createInvoiceShareLink(jobId);
            if (response?.code !== 200 || !response?.message?.url) {
                throw new Error(typeof response?.message === 'string' ? response.message : t('invoiceShare.linkCouldNotBePrepared'));
            }
            setShareUrl(response.message.url);
            if (response.message.invoiceNumber) setIssuedNumber(response.message.invoiceNumber);
        } catch (error) {
            setLinkError(error?.message || t('invoiceShare.linkCouldNotBePrepared'));
        } finally {
            setPreparing(false);
        }
    };

    useEffect(() => {
        if (!invoice) return;
        setRecipient(invoice.email || '');
        setMessageEdited(false);
        prepareShareLink();
        // Opening a different invoice must prepare its own secure link.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, invoiceNumber]);

    // Prefill from the saved company template (or the built-in default while
    // it loads) — but never overwrite text the user has already edited.
    useEffect(() => {
        if (!invoice || messageEdited) return;
        setMessage(renderInvoiceMessage(invoiceMessageTemplate, { clientName, invoiceNumber, jobNumber: jobId }));
    }, [invoice, invoiceMessageTemplate, clientName, invoiceNumber, jobId, messageEdited]);

    if (!invoice) return null;

    const shareMessage = (channel) => {
        if (!shareUrl) return;
        const text = withInvoiceLink(message, shareUrl, t('defaultMessages.viewInvoiceLink'));
        if (channel === 'whatsapp') {
            window.open(buildWhatsAppInvoiceUrl(invoice.phone, text), '_blank', 'noopener,noreferrer');
            setShared('whatsapp');
            return;
        }
        // A viber:// URL fails silently when the app is not installed, so say so
        // rather than leave the user waiting for something that will not happen.
        window.location.href = buildViberInvoiceUrl(invoice.phone, text);
        setShared('viber');
    };

    const sendEmail = async () => {
        setSending(true);
        try {
            // The PDF is attached to the email; the link is only substituted
            // where the template explicitly asks for it.
            const body = message.includes(LINK_PLACEHOLDER) && shareUrl ? withInvoiceLink(message, shareUrl, t('defaultMessages.viewInvoiceLink')) : message;
            const response = await emailInvoice({ jobId: Number(jobId), email: recipient, message: body });
            if (response?.code !== 200) throw new Error(response?.message || t('documentShare.emailFailed'));
            toast.success(t('invoiceShare.sentTo', { number: invoiceNumber, recipient }));
        } catch (error) {
            toast.error(error?.message || t('invoiceShare.emailCouldNotBeSent'));
        } finally {
            setSending(false);
        }
    };

    return <div className="qap-backdrop" onMouseDown={onClose}>
        <aside className="qap" onMouseDown={(event) => event.stopPropagation()} aria-label={t('invoiceShare.title')}>
            <div className="qap-head"><div><b>{t('invoiceShare.title')}</b><span>{invoiceNumber} · {t('jobs.jobNumber', { id: jobId })} · {clientName}</span></div><button type="button" onClick={onClose}>×</button></div>
            <div className="qap-form invoice">
                <label>
                    {t('documentShare.recipientEmail')}
                    <input type="email" value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="client@example.com" />
                    {!recipient && <small className="qap-hint">{t('documentShare.noEmailHint')}</small>}
                </label>
                <label>
                    {t('documentShare.message')}
                    <textarea rows="7" value={message} onChange={(event) => { setMessage(event.target.value); setMessageEdited(true); }} />
                    <small className="qap-hint">{t('invoiceShare.prefillHint')}</small>
                </label>
                {preparing && <div className="qap-share-note neutral">{t('invoiceShare.preparingLink')}</div>}
                {linkError && <div className="qap-share-note error">{linkError} <button type="button" onClick={prepareShareLink}>{t('documentShare.tryAgain')}</button></div>}
                {shared === 'viber' &&
                    <div className="qap-share-note neutral">
                        {t('invoiceShare.viberOpened')}
                    </div>
                }
                {shared === 'whatsapp' &&
                    <div className="qap-share-note neutral">{t('invoiceShare.whatsappOpened')}</div>
                }
                {!hasPhone && <div className="qap-share-note neutral">{t('documentShare.noPhoneHint')}</div>}
                <div className="qap-send">
                    <button disabled={sending || preparing || !shareUrl} onClick={() => shareMessage('whatsapp')}>WhatsApp</button>
                    <button disabled={sending || preparing || !shareUrl} onClick={() => shareMessage('viber')}>Viber</button>
                    <button disabled={sending || !recipient} onClick={() => setConfirmSend(true)}>{sending ? t('documentShare.sending') : t('common.email')}</button>
                </div>
            </div>
            <ConfirmationModal display={confirmSend} toggle={setConfirmSend} tone="primary"
                title={t('invoiceShare.confirmTitle')} confirmText={t('invoiceShare.send')} cancelText={t('common.cancel')}
                message={t('invoiceShare.willBeEmailedTo', { number: invoiceNumber, recipient })}
                confirmAction={sendEmail} />
        </aside>
    </div>;
};

export default InvoiceSharePanel;
